import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { absenceService } from '@/api/domains/absence/absence.service';
import type { EmployeeForAbsence } from '@/api/domains/absence/absence.types';
import { useUI } from '@/hooks/ui';
import type { SelectorOption } from '@/components/ui/inputs/selector';


const PAGE_SIZE = 20;

export interface UseAbsenceEmployeeSelectorParams {
  /**
   * Mensaje de error a mostrar si falla la carga de colaboradores. Debe venir ya traducido
   * (ver Rule 5: el fallback de showApiError nunca puede ser una arrow function).
   */
  loadErrorMessage: string;
  /**
   * Texto del tooltip de los colaboradores bloqueados por sincronización con Rex Remuneraciones,
   * ya traducido. Si se omite, los colaboradores bloqueados quedan seleccionables — que es el
   * comportamiento deliberado de los ausentismos que resuelven el bloqueo por otra vía.
   * Solo licencia médica y vacaciones lo informan.
   */
  blockedTooltip?: string;
}

export interface UseAbsenceEmployeeSelectorResult {
  employeeOptions: SelectorOption[];
  employeeBlockedIds: Set<number>;
  hasBlockedEmployees: boolean;
  isLoadingEmployees: boolean;
  isLoadingMoreEmployees: boolean;
  employeeHasMore: boolean;
  handleEmployeeSearch: (search: string) => void;
  loadMoreEmployees: () => Promise<void>;
  /**
   * Marca un colaborador como el seleccionado para que no desaparezca de las opciones al
   * paginar o buscar. Devuelve el colaborador encontrado, o undefined si el id no está cargado.
   */
  selectEmployee: (employeeId: number) => EmployeeForAbsence | undefined;
}

/**
 * Carga paginada y buscable de colaboradores para los formularios de ingreso de ausentismos.
 * Fuente única del selector: usa el mismo endpoint que trae `isBlocked` y arma las opciones con
 * el contrato `disabled`/`disabledTooltip` que `SelectorOptionItem` renderiza en gris con tooltip.
 */
export function useAbsenceEmployeeSelector(
  { loadErrorMessage, blockedTooltip }: UseAbsenceEmployeeSelectorParams,
): UseAbsenceEmployeeSelectorResult {
  const { showApiError } = useUI();

  const [employees, setEmployees] = useState<EmployeeForAbsence[]>([]);
  const [employeePage, setEmployeePage] = useState(1);
  const [employeeHasMore, setEmployeeHasMore] = useState(false);
  const [employeeSearchText, setEmployeeSearchText] = useState('');
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);
  const [isLoadingMoreEmployees, setIsLoadingMoreEmployees] = useState(false);
  const selectedEmployeeRef = useRef<EmployeeForAbsence | null>(null);

  const loadEmployees = useCallback(
    async (page: number, search: string, append: boolean = false) => {
      if (append) {
        setIsLoadingMoreEmployees(true);
      } else {
        setIsLoadingEmployees(true);
      }
      try {
        const response = await absenceService.getEmployeesForAbsence({
          page,
          pageSize: PAGE_SIZE,
          searchText: search || undefined,
        });

        if (response.success && response.data) {
          const newItems = response.data.data ?? [];
          setEmployees((previous) => {
            let result = append ? [...previous, ...newItems] : newItems;
            const selected = selectedEmployeeRef.current;
            if (selected && !result.some((employee) => employee.id === selected.id)) {
              result = [selected, ...result];
            }
            return result;
          });
          setEmployeeHasMore(response.data.hasNextPage);
          setEmployeePage(page);
        } else {
          showApiError(response, loadErrorMessage);
        }
      } finally {
        if (append) {
          setIsLoadingMoreEmployees(false);
        } else {
          setIsLoadingEmployees(false);
        }
      }
    },
    [showApiError, loadErrorMessage],
  );

  useEffect(() => {
    void loadEmployees(1, '');
  }, [loadEmployees]);

  const loadMoreEmployees = useCallback(async () => {
    if (!isLoadingMoreEmployees && employeeHasMore) {
      await loadEmployees(employeePage + 1, employeeSearchText, true);
    }
  }, [isLoadingMoreEmployees, employeeHasMore, employeePage, employeeSearchText, loadEmployees]);

  const handleEmployeeSearch = useCallback(
    (search: string) => {
      setEmployeeSearchText(search);
      void loadEmployees(1, search);
    },
    [loadEmployees],
  );

  const employeeOptions = useMemo<SelectorOption[]>(
    () =>
      employees.map((employee) => ({
        value: String(employee.id),
        label: employee.fullName
          ? `${employee.fullName}${employee.employeeTaxIdentificationNumber ? ` (${employee.employeeTaxIdentificationNumber})` : ''}`
          : String(employee.id),
        supportingText: employee.companyName || undefined,
        disabled: blockedTooltip ? employee.isBlocked : undefined,
        disabledTooltip: blockedTooltip && employee.isBlocked ? blockedTooltip : undefined,
      })),
    [employees, blockedTooltip],
  );

  const employeeBlockedIds = useMemo<Set<number>>(
    () => new Set(
      employees
        .filter((employee) => employee.isBlocked)
        .map((employee) => employee.id),
    ),
    [employees],
  );

  const selectEmployee = useCallback(
    (employeeId: number) => {
      const found = employees.find((employee) => employee.id === employeeId);
      if (found) {
        selectedEmployeeRef.current = found;
      }
      return found;
    },
    [employees],
  );

  return {
    employeeOptions,
    employeeBlockedIds,
    hasBlockedEmployees: employeeBlockedIds.size > 0,
    isLoadingEmployees,
    isLoadingMoreEmployees,
    employeeHasMore,
    handleEmployeeSearch,
    loadMoreEmployees,
    selectEmployee,
  };
}

export default useAbsenceEmployeeSelector;
